/**
 * Sorts two room objects based on building, floor, name, room number, and seat.
 *
 * The sorting priority is as follows:
 * 1. `building` (lexicographical order)
 * 2. `floor` (natural sort order, supports alphanumeric values like "1", "2A", "3")
 * 3. `name` (lexicographical order)
 * 4. `roomNumber` (natural sort order, supports alphanumeric values like "101", "A1", "B2")
 * 5. `seat` (ascending numerical order)
 *
 * @typeParam T - An object type that includes `name`, `seat`, `floor`, `roomNumber`, and `building` properties.
 * @param roomA - The first room object to compare.
 * @param roomB - The second room object to compare.
 * @returns A negative number if `roomA` should come before `roomB`, a positive number if `roomA` should come after `roomB`, or 0 if they are considered equal.
 */
export function sortRooms<
  T extends {
    name: string;
    seat: number;
    floor: string;
    roomNumber: string | null;
    building: string;
  },
>(roomA: T, roomB: T): number {
  const buildingDiff = roomA.building.localeCompare(roomB.building);
  if (buildingDiff !== 0) return buildingDiff;

  const floorDiff = roomA.floor.localeCompare(roomB.floor, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  if (floorDiff !== 0) return floorDiff;

  const nameDiff = roomA.name.localeCompare(roomB.name);
  if (nameDiff !== 0) return nameDiff;

  const aRoomNumber = roomA.roomNumber ?? '';
  const bRoomNumber = roomB.roomNumber ?? '';
  const roomNumberDiff = aRoomNumber.localeCompare(bRoomNumber, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  if (roomNumberDiff !== 0) return roomNumberDiff;

  return roomA.seat - roomB.seat;
}
